import { useEffect, useRef, useState } from 'react';
import { classNames } from '~/utils/classNames';
import { Modal } from '~/components/ui/Modal';
import * as Sentry from '@sentry/remix';
import { useTranslation } from '~/lib/hooks/useTranslation';
import {
  techStackSelectionStore,
  type Framework,
  type Language,
  FRAMEWORK_CONFIGS,
  setFramework,
  setLanguage,
  type PackageManager,
  setPackageManager,
} from '~/lib/stores/techStackSelection';
import { useStore } from '@nanostores/react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Dropdown, type DropdownOption } from '~/components/ui/Dropdown';
import { frameworkService } from '~/lib/services/frameworkService';
import { CHECK_IGNORE_FILE } from '~/utils/fileIgnore';
import {
  Free_Git_Repo_Tokens,
  Pro_Git_Repo_Tokens,
  Free_Git_Repo_Size,
  Pro_Git_Repo_Size,
  Special_Users,
} from '~/utils/constants';
import { githubBranchStore, githubRepoStore, githubOwnerStore } from '~/lib/stores/integrations';
import { userStore } from '~/lib/stores/user';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { ProUpgradeModal } from '~/components/ui/ProUpgradeModal';
import { useStorageStatus } from '~/hooks/useStorageStatus';
import { toast } from 'react-toastify';
import { useFetcher } from '@remix-run/react';

interface GithubModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitRepo: (repoUrl: string, isLoading: boolean) => void;
  isUploadingToWb: boolean;
  githubCreds: {
    GITHUB_CLIENT_ID: string;
  };
  isChatStarted: boolean;
  isProcessingGithubRepo: boolean;
  setProcessingGithubRepo: (isProcessing: boolean) => void;
  githubRepoUsagePercent: number;
  setShowManageStorageModal: (show: boolean) => void;
  indexDBUsagePercent: number;
  paymentUrl: string;
}
interface GitHubTreeResponse {
  tree: Array<{
    type: string;
    path: string;
    size?: number;
  }>;
}

interface GitHubUser {
  login: string;
  email: string;
  avatar_url: string;
  name?: string;
}

interface GitHubBranch {
  name: string;
  commit: {
    sha: string;
    url: string;
  };
}

interface RepooSize {
  totalSize: number;
  totalSizeInMB: number;
}

export default function GithubModal({
  isOpen,
  onClose,
  onSubmitRepo,
  isUploadingToWb,
  githubCreds,
  isChatStarted,
  isProcessingGithubRepo,
  setProcessingGithubRepo,
  githubRepoUsagePercent,
  setShowManageStorageModal,
  indexDBUsagePercent,
  paymentUrl,
}: GithubModalProps) {
  const { t } = useTranslation();
  const userStoreData = useStore(userStore);
  const fetcher = useFetcher<{ tokens: Record<string, any> }>();

  const CHARS_PER_TOKEN = 5;
  const { isSubscribed, email } = userStoreData;
  const isSpecialUser = Special_Users.includes(email);
  const finalProGitRepoTokens = isSpecialUser ? 400000 : Pro_Git_Repo_Tokens;
  const finalProGitRepoSize = isSpecialUser ? 50 : Pro_Git_Repo_Size;

  const getMaxTokens = () => {
    return isSubscribed ? finalProGitRepoTokens : Free_Git_Repo_Tokens;
  };
  const MAX_CHARS = getMaxTokens() * CHARS_PER_TOKEN;
  const MAX_REPO_SIZE = isSubscribed ? finalProGitRepoSize : Free_Git_Repo_Size;

  const calculateRepoSize = async (
    owner: string,
    repo: string,
    branch: string,
    accessToken: string,
  ): Promise<RepooSize> => {
    try {
      return await Sentry.startSpan(
        {
          name: 'calculateRepoSize.GithubModal',
          op: 'calculateRepoSize.GithubModal',
        },
        async () => {
          try {
            const { data } = await axios.get<GitHubTreeResponse>(
              `https://api.github.com/repos/${owner}/${repo}/git/trees/${branch}?recursive=1`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  Accept: 'application/vnd.github.v3+json',
                },
              },
            );
            console.log('[calculateRepoSize] data:', data);
            return {
              totalSize: calculateTotalSize(data.tree),
              totalSizeInMB: calculateTotalSizeInMB(data.tree) / 1000000,
            };
          } catch {
            const { data } = await axios.get<GitHubTreeResponse>(
              `https://api.github.com/repos/${owner}/${repo}/git/trees/main?recursive=1`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  Accept: 'application/vnd.github.v3+json',
                },
              },
            );
            console.log('[calculateRepoSize] data:', data);
            return {
              totalSize: calculateTotalSize(data.tree),
              totalSizeInMB: calculateTotalSizeInMB(data.tree) / 1000000,
            };
          }
        },
      );
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error calculating repo size',
          owner,
          repo,
        },
      });
      console.error('Error calculating repo size:', error);
      throw error;
    }
  };

  const calculateTotalSize = (tree: any[]): number => {
    return tree.reduce((total, item) => {
      // Skip directories, ignored file types, node_modules, and hidden files
      const fileName = item.path.split('/').pop();

      if (
        item.path.includes('node_modules') ||
        CHECK_IGNORE_FILE(item.path) ||
        (fileName && fileName.startsWith('.')) ||
        fileName.includes('.svg') ||
        fileName == 'pnpm-lock.yaml' ||
        fileName == 'package-lock.json' ||
        fileName == 'bun.lockb' ||
        fileName.includes('LICENSE')
      ) {
        return total;
      }

      console.log('All files during repo size calculation', item.path, fileName, item.size);
      return total + (item.size || 0);
    }, 0);
  };

  const calculateTotalSizeInMB = (tree: any[]): number => {
    return tree.reduce((total, item) => {
      const fileName = item.path.split('/').pop();

      console.log('All files during repo size calculation', item.path, fileName, item.size);
      return total + (item.size || 0);
    }, 0);
  };

  const MAX_POLL_ATTEMPTS = 15;

  const validateRepoUrl = (url: string): boolean => {
    try {
      const parsedUrl = new URL(url);
      const pathParts = parsedUrl.pathname.split('/').filter(Boolean);
      console.log('parseUrl', parsedUrl, pathParts);

      return (
        parsedUrl.hostname === 'github.com' && pathParts.length >= 2 && Boolean(pathParts[0]) && Boolean(pathParts[1])
      );
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Invalid URL format',
          url,
        },
      });
      return false;
    }
  };

  const { indexDBUsageMB } = useStorageStatus(); // for sentry
  const [repoUrl, setRepoUrl] = useState('');
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [user, setUser] = useState<GitHubUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const pollInterval = useRef<NodeJS.Timeout>();
  const [isRepoUrlValid, setIsRepoUrlValid] = useState(true);
  const selectedFramework = techStackSelectionStore.get().framework;

  // console.log('selectedFramework >>>>>> ', selectedFramework, techStackSelectionStore.get().language);

  const [step, setStep] = useState<'url' | 'branch' | 'framework'>('url');
  const [isDetectingFramework, setIsDetectingFramework] = useState(false);
  const [branches, setBranches] = useState<GitHubBranch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isRemovingGithubIntegration, setIsRemovingGithubIntegration] = useState(false);
  const [showUpgradeButton, setShowUpgradeButton] = useState(false);
  const [showProUpgradeModal, setShowProUpgradeModal] = useState(false);
  const packageManager = techStackSelectionStore.get().packageManager;
  const techStackSelectionStoreData = useStore(techStackSelectionStore);

  const OAUTH_SCOPE = 'repo workflow read:org user user:email';

  const isLanguageKey = (lang: string): lang is Language => {
    return ['ts', 'js'].includes(lang);
  };

  const packageManagerOptions: DropdownOption[] = [
    { value: 'npm', label: 'npm' },
    { value: 'yarn', label: 'yarn' },
    { value: 'pnpm', label: 'pnpm' },
  ];

  const frameworkOptions = frameworkService.getFrameworkOptions();

  const checkGithubToken = () => {
    const pollCount = { current: 0 };
    console.log('[int] Starting token check');
    setIsAuthenticating(true);

    const checkForToken = async (): Promise<boolean> => {
      const token = selectionStore.get().githubAccessToken;
      console.log('[int] Current github token:', token ? 'exists' : 'null');

      if (token) {
        console.log('[checkForToken] Found token, validating...');

        const isValid = await fetchUserDetails(token);
        console.log('[checkForToken] Token validation result:', isValid);

        if (!isValid) {
          console.log('[checkForToken] Token invalid, clearing state');
          selectionStore.setKey('githubAccessToken', '');
          setError('GitHub token is invalid or expired. Please reconnect.');
          setAccessToken(null);
          setUser(null);
          setIsAuthenticating(false);
          return false;
        }

        console.log('[checkForToken] Token valid, setting access token');
        setAccessToken(token);
        setIsAuthenticating(false);
        return true;
      }

      pollCount.current += 1;
      console.log('[checkForToken] Poll attempt:', pollCount.current);

      if (pollCount.current >= MAX_POLL_ATTEMPTS) {
        console.log('[checkForToken] Max poll attempts reached');
        setIsAuthenticating(false);
        return false;
      }

      return false;
    };

    // Set up polling
    const interval = setInterval(async () => {
      console.log('[checkGithubToken] Polling for token...');

      const result = await checkForToken();

      if (result) {
        console.log('[checkGithubToken] Token check successful, clearing interval');
        clearInterval(interval);
      }
    }, 3000);

    return interval;
  };

  const fetchUserDetails = async (token: string) => {
    console.log('[fetchUserDetails] Starting user details fetch');

    try {
      return await Sentry.startSpan(
        {
          name: 'fetchUserDetails.GithubModal',
          op: 'fetchUserDetails.GithubModal',
        },
        async () => {
          const response = await fetch('https://api.github.com/user', {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/vnd.github+json',
            },
          });

          if (response.ok) {
            const userData = (await response.json()) as GitHubUser;

            // console.log('>>>[fetchUserDetails] userData: ', userData);
            setUser(userData);
            setAccessToken(token);
            return true;
          }

          return false;
        },
      );
    } catch (error) {
      console.error('Error fetching user details:', error);

      Sentry.captureException(error, {
        extra: {
          message: 'Error fetching GitHub user details',
          token: token ? 'token exists' : 'no-token',
        },
      });

      return false;
    }
  };

  const handleGithubLogin = () => {
    setError(''); // Clear any existing errors

    // Include redirect_uri so the OAuth code is bound to the correct environment/domain.
    const redirectUri = (window as any)?.ENV?.NEXT_PUBLIC_GITHUB_REDIRECT_URI as string | undefined;

    const params = new URLSearchParams({
      client_id: githubCreds.GITHUB_CLIENT_ID,
      scope: OAUTH_SCOPE,
      prompt: 'select_account', // Forces GitHub to show account picker
      ...(redirectUri ? { redirect_uri: redirectUri } : {}),
    });

    window.open(`https://github.com/login/oauth/authorize?${params.toString()}`, '_blank');
    pollInterval.current = checkGithubToken();
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (fetcher.state === 'idle' && !fetcher.data) {
      const token = selectionStore.get().githubAccessToken || '';
      fetcher.submit(
        { provider: 'github', localAccessToken: token },
        { method: 'POST', action: '/api/access-token', encType: 'application/json' },
      );
    }
  }, [isOpen, fetcher]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    console.log('[int] Modal opened, validating stored token');

    if (fetcher.data?.tokens) {
      const { github } = fetcher.data.tokens;
      console.log('[int] fetching token for github', github);

      if (github) {
        selectionStore.setKey('githubAccessToken', github.accessToken);
      }
    }

    const validateStoredToken = async () => {
      const token = selectionStore.get().githubAccessToken;

      console.log('[int] Stored token:', token ? 'exists' : 'null');

      if (token) {
        setIsAuthenticating(true);

        const isValid = await fetchUserDetails(token);
        console.log('[Modal] Token validation result:', isValid);

        if (!isValid) {
          await removeGithubIntegration();
        }

        setIsAuthenticating(false);
      }
    };

    validateStoredToken();
  }, [isOpen, fetcher.data]);

  useEffect(() => {
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, []);

  const extractRepoInfo = (url: string) => {
    try {
      const parsedUrl = new URL(url);
      const parts = parsedUrl.pathname.replace(/^\/|\/$/g, '').split('/');

      if (parts.length < 2) {
        return null;
      }

      const owner = parts[0];
      const repo = parts[1].replace(/\.git$/, '');

      if (!owner || !repo) {
        return null;
      }

      return { owner, repo };
    } catch (err) {
      Sentry.captureException(err, {
        extra: {
          message: 'Error extracting repo info',
          url,
        },
      });
      console.error('>>>[extractRepoInfo] error: ', err);
      return null;
    }
  };

  useEffect(() => {
    setIsLoading(isUploadingToWb);
    console.log('setIsLoading', isUploadingToWb);
  }, [isUploadingToWb]);

  const formatGitUrl = (url: string): string => {
    // Remove any tree references and trailing slashes
    return url
      .replace(/\/tree\/[^/]+/, '') // Remove /tree/branch-name
      .replace(/\/$/, '') // Remove trailing slash
      .replace(/\.git$/, ''); // Remove .git if present
  };

  const fetchBranches = async (owner: string, repo: string) => {
    try {
      return await Sentry.startSpan(
        {
          name: 'validateGithubRepository',
          op: 'validateGithubRepository',
        },
        async () => {
          let allBranches: GitHubBranch[] = [];
          let page = 1;
          const perPage = 100; // Max items per page
          let hasMorePages = true;

          while (hasMorePages) {
            try {
              const response = await axios.get<GitHubBranch[]>(
                `https://api.github.com/repos/${owner}/${repo}/branches`,
                {
                  headers: {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: 'application/vnd.github.v3+json',
                  },
                  params: {
                    per_page: perPage,
                    page,
                  },
                },
              );

              allBranches = allBranches.concat(response.data);

              // Check for more pages in Link header (axios stores headers as lowercase keys)
              const linkHeader = response.headers.link;

              if (typeof linkHeader === 'string' && linkHeader.includes('rel="next"')) {
                hasMorePages = true;
                page++;
              } else {
                hasMorePages = false;
              }
            } catch (error: any) {
              let status, errorMessage;

              if (axios.isAxiosError(error) && error.response) {
                status = error.response.status;
                errorMessage = error.response.data?.message || error.message;
              } else {
                status = 400;
                errorMessage = error.message || String(error);
              }

              console.log('>>>[fetchBranches] error:', status, errorMessage);
              throw new Error(JSON.stringify({ status, message: errorMessage }));
            }
          }

          console.log('>>>[fetchBranches] allBranches: ', allBranches);
          return allBranches;
        },
      );
    } catch (error) {
      console.error('Error fetching branches:', error);

      Sentry.captureException(error, {
        extra: {
          message: 'Error validating GitHub repository',
          owner,
          repo,
        },
      });

      throw error;
    }
  };

  const handleSubmit = async () => {
    if (githubRepoUsagePercent >= 100 || indexDBUsagePercent >= 100) {
      setShowManageStorageModal(true);
      return;
    }

    if (isChatStarted) {
      return;
    }

    if (step === 'url') {
      console.log('[github] handleSubmit: ', repoUrl);
      setIsLoading(true);
      setProcessingGithubRepo(true);

      if (!repoUrl) {
        setError(t('modals.github.enterRepositoryUrl', 'Please enter a repository URL'));
        setIsLoading(false);
        setProcessingGithubRepo(false);
        setIsRepoUrlValid(false);
        return;
      }

      try {
        if (!repoUrl.startsWith('https://github.com/')) {
          setError(t('modals.github.invalidUrl'));
          setIsRepoUrlValid(false);
          setIsLoading(false);
          setProcessingGithubRepo(false);
          return;
        }

        const formattedUrl = formatGitUrl(repoUrl);
        console.log('>>>[formatGitUrl] formattedUrl: ', formattedUrl);

        new URL(formattedUrl);

        const repoInfo = extractRepoInfo(formattedUrl);
        console.log('>>>[fetchRepositoryContent] repoInfo: ', repoInfo);

        if (!repoInfo) {
          setError(
            t(
              'modals.github.invalidRepositoryUrl',
              'Invalid repository URL. Please enter a URL in the format: https://github.com/owner/repository',
            ),
          );
          setIsRepoUrlValid(false);
          setIsLoading(false);
          setProcessingGithubRepo(false);
          return;
        }

        setRepoUrl(formattedUrl);

        // Fetch branches and repo info
        const [branchList, repoResponse] = await Promise.all([
          fetchBranches(repoInfo.owner, repoInfo.repo),
          axios
            .get(`https://api.github.com/repos/${repoInfo.owner}/${repoInfo.repo}`, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/vnd.github.v3+json',
              },
            })
            .catch((err) => {
              console.error('Error fetching repo details:', err);
              return { data: { default_branch: '' } };
            }),
        ]);

        const defaultBranch = repoResponse?.data?.default_branch;
        console.log('>>>[handleSubmit] branchList: ', branchList);
        console.log('>>>[handleSubmit] defaultBranch: ', defaultBranch);

        if (branchList && branchList.length > 0) {
          setBranches(branchList);

          const foundDefault = branchList.find((b) => b.name === defaultBranch);

          setSelectedBranch(foundDefault ? foundDefault.name : branchList[0].name);
        } else {
          setBranches([]);
          setSelectedBranch('');
        }

        // Move to branch selection step
        setStep('branch');
        setIsLoading(false);
        setProcessingGithubRepo(false);
        setError('');
      } catch (error) {
        let status, errorMessage;

        try {
          const parsed = JSON.parse((error as any).message);
          status = parsed.status;
          errorMessage = parsed.message;
        } catch (parseError) {
          errorMessage = (error as any).message;
        }

        console.log(
          '>>>[handleSubmit] Final values - status: ',
          status,
          'errorMessage: ',
          errorMessage,
          'status type: ',
          typeof status,
          'errorMessage type: ',
          typeof errorMessage,
        );

        setIsRepoUrlValid(false);

        // Now use status and errorMessage as before
        const numericStatus = Number(status);

        switch (numericStatus) {
          case 400: {
            setError(errorMessage);
            break;
          }
          case 401: {
            setError(errorMessage);
            break;
          }
          case 403: {
            if (errorMessage && errorMessage.includes('OAuth App access restrictions')) {
              setError(errorMessage);
              break;
            } else {
              setError(errorMessage);
              break;
            }
          }
          case 404: {
            setError(
              t('modals.github.repositoryNotFound', 'Repository not found. Please check the URL and try again.'),
            );
            break;
          }
          case 422: {
            setError(errorMessage);
            break;
          }
          case 500: {
            setError(t('modals.github.internalServerError', 'Internal Server Error. Please try again later.'));
            break;
          }
          case 503: {
            setError(
              t(
                'modals.github.serviceUnavailable',
                'Service Unavailable. GitHub is currently unavailable. Please try again later.',
              ),
            );
            break;
          }
          default: {
            setError(
              t('modals.github.errorWithStatus', 'Error {{status}}: {{message}}', { status, message: errorMessage }),
            );
            break;
          }
        }

        setIsLoading(false);
        setProcessingGithubRepo(false);
      }
    } else if (step === 'branch') {
      // Handle branch selection → detect framework and move to framework step
      setIsLoading(true);
      setIsDetectingFramework(true);
      setError('');

      try {
        const repoInfo = extractRepoInfo(repoUrl);

        if (!repoInfo) {
          throw new Error('Invalid repository info');
        }

        const token = selectionStore.get().githubAccessToken;

        if (!token) {
          throw new Error('GitHub authentication token not found');
        }

        // Auto-detect framework from the selected branch
        const detected = await frameworkService.detectFrameworkFromRepo(
          repoInfo.owner,
          repoInfo.repo,
          selectedBranch,
          token,
        );

        console.log('>>>[handleSubmit] Detected framework:', detected);

        // Pre-populate the dropdowns with detected values
        setFramework(detected.framework);
        setLanguage(detected.language);
        setPackageManager(detected.packageManager);

        setStep('framework');
        setIsLoading(false);
        setIsDetectingFramework(false);
      } catch (error) {
        console.error('Error detecting framework:', error);
        Sentry.captureException(error, {
          extra: { message: 'Error detecting framework from repo', url: repoUrl },
        });

        // On error, still move to framework step with defaults
        setStep('framework');
        setIsLoading(false);
        setIsDetectingFramework(false);
      }
    } else {
      // Handle framework step submission (final submit)
      setIsLoading(true);
      setProcessingGithubRepo(true);

      try {
        const repoInfo = extractRepoInfo(repoUrl);
        console.log('>>>[handleSubmit] Extracted repo info:', repoInfo);

        if (!repoInfo) {
          throw new Error('Invalid repository info');
        }

        const token = selectionStore.get().githubAccessToken;

        if (!token) {
          console.log('>>>[handleSubmit] No token found, throwing error');
          throw new Error('GitHub authentication token not found');
        }

        const { totalSize, totalSizeInMB } = await calculateRepoSize(
          repoInfo.owner,
          repoInfo.repo,
          selectedBranch,
          token,
        );

        Sentry.captureMessage(
          `PRE_IMPORT AI_TOKENS_IN_REPO: ${Math.ceil(totalSize / CHARS_PER_TOKEN).toLocaleString()} TOTAL_SIZE_IN_MB: ${totalSizeInMB.toFixed(2)} CURRENT_INDEXED_DB_USAGE_MB: ${indexDBUsageMB.toFixed(2)} REPO_URL: ${repoUrl} BRANCH: ${selectedBranch}`,
          'info',
        );

        if (totalSize > MAX_CHARS) {
          const maxTokens = getMaxTokens();
          const errorMsg = `Repository too large, ${Math.ceil(totalSize / CHARS_PER_TOKEN).toLocaleString()} tokens. Maximum is ${maxTokens.toLocaleString()}.`;
          Sentry.captureMessage(
            `REPO_TOO_LARGE (TOKEN_LIMIT_EXCEEDED): ${errorMsg} REPO_URL: ${repoUrl} BRANCH: ${selectedBranch}`,
            'warning',
          );
          setError(errorMsg);
          setIsRepoUrlValid(false);
          setIsLoading(false);
          setProcessingGithubRepo(false);

          if (totalSize / CHARS_PER_TOKEN <= finalProGitRepoTokens && !isSubscribed) {
            setShowUpgradeButton(true);
          }

          return;
        }

        console.log(
          '>>>[handleSubmit] Checking repo size in MB - MAX_REPO_SIZE:',
          MAX_REPO_SIZE,
          'totalSizeInMB:',
          totalSizeInMB,
        );

        if (totalSizeInMB > MAX_REPO_SIZE) {
          const errorMsg = `Repository is too large (${totalSizeInMB.toFixed(2)} MB). Maximum size is ${MAX_REPO_SIZE} MB.`;
          console.log('>>>[handleSubmit] MB size error message:', errorMsg);
          Sentry.captureMessage(
            `REPO_TOO_LARGE: ${errorMsg} REPO_URL: ${repoUrl} BRANCH: ${selectedBranch}`,
            'warning',
          );
          setError(errorMsg);
          setIsRepoUrlValid(false);
          setIsLoading(false);
          setProcessingGithubRepo(false);
          return;
        }

        console.log('>>>[handleSubmit] Repository size checks passed, storing branch info');

        // Store the selected branch
        githubBranchStore.set(selectedBranch);
        githubRepoStore.set(repoInfo.repo);
        githubOwnerStore.set(repoInfo.owner);

        onSubmitRepo(repoUrl, isLoading);
        setError('');
        setIsLoading(false);
      } catch (error) {
        console.error('Error processing repository:', error);

        Sentry.captureException(error, {
          extra: {
            url: repoUrl,
            indexDBUsageMB,
          },
        });
        setError(t('modals.github.invalidFormat'));
        setIsLoading(false);
      }
    }
  };

  const handleRepoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setRepoUrl(url);

    const isValid = validateRepoUrl(url);
    setIsRepoUrlValid(isValid);
    setError(isValid ? '' : t('modals.github.invalidUrl'));
  };

  const removeGithubIntegration = async () => {
    if (isRemovingGithubIntegration) {
      return;
    }

    setIsRemovingGithubIntegration(true);

    try {
      await axios.delete('/api/user-integration', {
        data: {
          provider: 'github',
        },
      });

      const storedToken = selectionStore.get().githubAccessToken;

      if (storedToken) {
        await revokeGithubToken(storedToken);
      }

      selectionStore.setKey('githubAccessToken', '');
      setAccessToken(null);
      setUser(null);
    } catch (error) {
      console.error('Error deleting user integration from GithubModal:', error);
      Sentry.captureException(error);
      toast.error('Failed to disconnect GitHub. Please try again or refresh the page.');
    } finally {
      setIsRemovingGithubIntegration(false);
    }
  };

  const revokeGithubToken = async (token: string) => {
    Sentry.startSpan({ name: 'revokeGithubToken' }, async (span) => {
      try {
        if (typeof token !== 'string' || !token) {
          console.error('Invalid token: Token must be a non-empty string');
          return false;
        }

        const response = await axios.post('/api/github/revoke', { token });

        console.log('>>>[revokeGithubToken] response: ', response);

        return response.status === 200;
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            message: 'Failed to revoke GitHub token',
            token,
          },
        });
        console.error('Failed to revoke GitHub token:', error);
        return false;
      }
    });
  };

  useEffect(() => {
    if (isChatStarted) {
      setStep('url');
    }
  }, [isChatStarted]);

  return (
    <Modal isOpen={isOpen} onClose={isProcessingGithubRepo ? () => void 0 : onClose}>
      <div className="w-full max-w-[480px]">
        <div className="bg-dualite-background-tertiary text-dualite-text-primary rounded-lg border border-dualite-border-primary shadow-2xl relative w-full flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-dualite-border-primary flex justify-between items-center bg-dualite-background-tertiary shrink-0">
            <h3 className="text-[11px] font-medium tracking-wider">{t('modals.github.title')}</h3>
            <button
              onClick={onClose}
              disabled={isProcessingGithubRepo}
              className={`text-dualite-text-modal-caption hover:text-dualite-text-white transition-colors ml-2 ${isProcessingGithubRepo ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="i-ph:x-bold text-sm" />
            </button>
          </div>

          <div className="p-6">
            {isAuthenticating ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="i-ph:circle-notch w-8 h-8 animate-spin text-dualite-primary-start" />
                <p className="text-sm text-dualite-text-secondary">{t('modals.github.authenticating')}</p>
              </div>
            ) : accessToken && user ? (
              <div className="space-y-4">
                {isChatStarted && (
                  <div className="dark:bg-yellow-500/10 bg-yellow-600/5 dark:text-yellow-500 text-yellow-700 p-3 rounded-md text-sm mb-4">
                    {t('modals.github.chatInProgress')}
                  </div>
                )}
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full dark:bg-indigo-600 bg-indigo-500 flex items-center justify-center text-white font-medium overflow-hidden">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt="Profile" className="w-full h-full object-cover rounded-full" />
                    ) : (
                      user.name?.charAt(0).toUpperCase() || user.email?.split('@')[0].charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 ml-4">
                    <p className="text-[12px] font-medium text-dualite-text-primary">
                      {user.email || user.name || user?.login || 'GitHub User'}
                    </p>
                    <p className="text-[11px] text-dualite-text-modal-caption mt-0.5">
                      {t('modals.github.accountConnected')}
                    </p>
                  </div>
                  <div
                    className={classNames(
                      'flex flex-row items-center p-2 rounded-md transition-colors',
                      isRemovingGithubIntegration
                        ? 'opacity-60 cursor-not-allowed text-dualite-text-modal-secondary dark:bg-red-500/20 bg-red-600/10'
                        : 'cursor-pointer text-dualite-text-modal-secondary hover:text-dualite-text-white dark:bg-red-500/20 bg-red-600/10 hover:dark:bg-red-500/30 hover:bg-red-600/20',
                    )}
                  >
                    <div className="i-ph:sign-out w-3.5 h-3.5 dark:text-red-400 text-red-600" />
                    <button
                      onClick={removeGithubIntegration}
                      disabled={isRemovingGithubIntegration}
                      className="ml-1.5 dark:text-red-400 text-red-600 text-[11px] transition-colors"
                    >
                      {isRemovingGithubIntegration ? t('modals.github.disconnecting') : t('modals.github.disconnect')}
                    </button>
                  </div>
                </div>
                <hr className="border-t border-dualite-border-primary" />
                {isChatStarted && (
                  <div className="dark:bg-yellow-500/10 bg-yellow-600/5 dark:text-yellow-500 text-yellow-700 p-3 rounded-md text-sm mb-4">
                    {t('modals.github.chatInProgress')}
                  </div>
                )}
                {step === 'url' ? (
                  <div className="space-y-2">
                    <label className="block text-[11px] font-medium text-dualite-text-gradient-end uppercase tracking-wider">
                      {t('modals.github.repoLink')}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={repoUrl}
                        onChange={handleRepoUrlChange}
                        disabled={isChatStarted}
                        className={classNames(
                          'w-full bg-dualite-background-button border rounded-md px-3 py-2 text-[13px] text-dualite-text-modal-primary focus:outline-none transition-all duration-200',
                          isRepoUrlValid
                            ? 'border-dualite-border-secondary'
                            : 'dark:border-red-500/40 border-red-600/30 focus:dark:border-red-500/60 focus:border-red-600/50',
                          isChatStarted ? 'opacity-40 cursor-not-allowed' : 'hover:border-dualite-border-secondary',
                        )}
                        placeholder="https://github.com/owner/repo"
                      />
                      {!isRepoUrlValid && repoUrl && repoUrl.length ? (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 dark:text-red-400 text-red-600">
                          <div className="i-ph:warning-circle-fill" />
                        </span>
                      ) : (
                        isRepoUrlValid &&
                        repoUrl &&
                        repoUrl.length > 0 && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 dark:text-green-400 text-green-600">
                            <div className="i-ph:check-circle-fill" />
                          </span>
                        )
                      )}
                    </div>
                  </div>
                ) : step === 'branch' ? (
                  <div className="space-y-4">
                    {!isChatStarted && (
                      <motion.div
                        className="w-full flex flex-col gap-3"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, delay: 0.05 }}
                      >
                        <Dropdown
                          id="branch-select"
                          options={branches.map((b) => ({ value: b.name, label: b.name }))}
                          value={selectedBranch || (branches.length > 0 ? branches[0].name : '')}
                          onChange={(value) => setSelectedBranch(value)}
                          label={t('modals.github.selectBranch')}
                          className="w-full"
                          placeholder={t('modals.github.selectBranch')}
                          disabled={branches.length === 0}
                        />
                        <p className="text-[11px] text-dualite-text-modal-caption leading-relaxed">
                          {t('modals.github.branchDescription')}{' '}
                          {repoUrl && (
                            <a
                              href={`${repoUrl}/branches`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-dualite-text-modal-tertiary underline underline-offset-2 hover:text-dualite-text-modal-secondary transition-colors duration-150"
                            >
                              {t('modals.github.viewAllBranches')}
                            </a>
                          )}
                        </p>
                      </motion.div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {!isChatStarted && (
                      <motion.div
                        className="w-full flex flex-col gap-3"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, delay: 0.05 }}
                      >
                        <div className="flex items-end gap-3 w-full">
                          <Dropdown
                            id="framework-select"
                            options={frameworkOptions}
                            value={techStackSelectionStoreData.framework}
                            onChange={(value) => {
                              console.log('value: ', value);

                              const framework = value as Framework;

                              const config = FRAMEWORK_CONFIGS[framework];
                              console.log('config: ', config, techStackSelectionStoreData.language);

                              setFramework(framework);

                              const currentLang = techStackSelectionStoreData.language;

                              if (!config.languages.includes(currentLang)) {
                                setFramework(framework);
                              }
                            }}
                            label={t('modals.github.framework')}
                            className="flex-1"
                          />
                          <Dropdown
                            id="language-select"
                            options={[
                              {
                                value: 'ts',
                                label: 'TypeScript',
                                icon: (
                                  <div className="w-5 h-5 flex items-center justify-center">
                                    <svg
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M23.429 3.50909e-06H0.570001C0.495063 3.39377e-06 0.420861 0.01478 0.35164 0.0434876C0.282419 0.0721953 0.219539 0.114271 0.166596 0.167306C0.113654 0.220341 0.0716885 0.283296 0.0431022 0.352567C0.0145159 0.421838 -0.000130591 0.496066 8.78816e-07 0.571003V23.43C-0.000130854 23.505 0.0145486 23.5793 0.0431974 23.6487C0.0718461 23.718 0.113901 23.781 0.166949 23.8341C0.219998 23.8871 0.282996 23.9292 0.352333 23.9578C0.421669 23.9865 0.495979 24.0011 0.571001 24.001H23.43C23.505 24.0011 23.5793 23.9865 23.6487 23.9578C23.718 23.9292 23.781 23.8871 23.8341 23.8341C23.8871 23.781 23.9292 23.718 23.9578 23.8341C23.9865 23.781 24.0011 23.718 24.001 23.43V0.570004C24.001 0.494981 23.9862 0.420697 23.9574 0.351411C23.9287 0.282125 23.8865 0.219199 23.8333 0.166244C23.7802 0.113288 23.7171 0.0713441 23.6477 0.0428168C23.5784 0.0142895 23.504 -0.000259726 23.429 3.50909e-06ZM14.286 12.826H11.429V21.714H9.143V12.826H6.286V10.857H14.286V12.826ZM14.926 21.206V18.831C14.926 18.831 16.224 19.809 17.781 19.809C19.338 19.809 19.278 18.791 19.278 18.651C19.278 17.174 14.866 17.174 14.866 13.9C14.866 9.448 21.295 11.205 21.295 11.205L21.215 13.321C21.215 13.321 20.137 12.602 18.919 12.602C17.701 12.602 17.262 13.182 17.262 13.8C17.262 15.397 21.714 15.238 21.714 18.452C21.714 23.402 14.926 21.207 14.926 21.207"
                                        fill="currentColor"
                                      />
                                    </svg>
                                  </div>
                                ),
                                disabled: !FRAMEWORK_CONFIGS[selectedFramework].languages.includes('ts' as Language),
                              },
                              {
                                value: 'js',
                                label: 'JavaScript',
                                icon: (
                                  <div className="w-5 h-5 flex items-center justify-center">
                                    <svg
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M0 0H24V24H0V0ZM18.347 20.12C17.234 20.12 16.605 19.54 16.122 18.75L14.289 19.815C14.951 21.123 16.304 22.121 18.399 22.121C20.541 22.121 22.136 21.009 22.136 18.978C22.136 17.095 21.054 16.258 19.138 15.435L18.574 15.194C17.606 14.774 17.187 14.501 17.187 13.824C17.187 13.277 17.607 12.858 18.267 12.858C18.914 12.858 19.331 13.131 19.718 13.824L21.474 12.697C20.731 11.39 19.701 10.891 18.267 10.891C16.253 10.891 14.964 12.179 14.964 13.871C14.964 15.706 16.044 16.575 17.672 17.268L18.236 17.51C19.265 17.96 19.878 18.234 19.878 19.007C19.878 19.653 19.281 20.12 18.347 20.12ZM9.607 20.105C8.832 20.105 8.509 19.575 8.155 18.945L6.319 20.057C6.851 21.183 7.897 22.117 9.702 22.117C11.701 22.117 13.07 21.054 13.07 18.719V11.019H10.815V18.689C10.815 19.816 10.347 20.105 9.606 20.105"
                                        fill="currentColor"
                                      />
                                    </svg>
                                  </div>
                                ),
                                disabled: !FRAMEWORK_CONFIGS[selectedFramework].languages.includes('js' as Language),
                              },
                            ]}
                            value={techStackSelectionStoreData.language}
                            onChange={(value) => {
                              console.log('value: ', value, techStackSelectionStoreData.language, isLanguageKey(value));

                              if (isLanguageKey(value)) {
                                console.log('setting language: ', value);
                                setLanguage(value);
                              }
                            }}
                            label={t('modals.github.language')}
                            className="flex-1"
                          />
                        </div>
                        <Dropdown
                          id="package-manager-select"
                          options={packageManagerOptions}
                          value={packageManager}
                          onChange={(value) => {
                            setPackageManager(value as PackageManager);
                          }}
                          label={t('modals.github.packageManager')}
                          className="w-full"
                        />
                      </motion.div>
                    )}
                  </div>
                )}

                {error && (
                  <div className="flex items-center justify-between gap-3 p-3 mt-2 mb-1 text-sm dark:bg-red-500/10 bg-red-600/5 border dark:border-red-500/20 border-red-600/10 rounded-md">
                    <div className="flex items-start gap-2">
                      <div className="i-ph:warning-circle-fill dark:text-red-500 text-red-600 text-lg flex-shrink-0 mt-0.5" />
                      <p className="dark:text-red-400 text-red-600 leading-tight">{error}</p>
                    </div>
                    {showUpgradeButton && (
                      <button
                        onClick={() => setShowProUpgradeModal(true)}
                        className="shrink-0 px-3 py-1.5 text-xs font-semibold text-dualite-text-cta bg-dualite-background-cta rounded-md hover:bg-dualite-background-cta-hover transition-colors shadow-sm whitespace-nowrap"
                      >
                        {t('modals.github.upgrade', 'Upgrade for higher limits')}
                      </button>
                    )}
                  </div>
                )}

                <div className="flex text-xs w-full">
                  {step !== 'url' && (
                    <button
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md transition-all duration-200 border border-dualite-border-primary hover:border-dualite-border-secondary bg-dualite-background-button hover:bg-dualite-background-button-hover group mr-auto"
                      onClick={() => {
                        setError('');

                        if (step === 'framework') {
                          setStep('branch');
                        } else {
                          setStep('url');
                        }
                      }}
                    >
                      <div className="i-ph:arrow-left text-sm text-dualite-text-modal-secondary transition-transform duration-200 group-hover:-translate-x-0.5" />
                      <span className="text-dualite-text-secondary transition-transform duration-200 group-hover:-translate-x-0.5">
                        {step === 'framework' ? t('modals.github.back', 'Back') : t('modals.github.backToUrl')}
                      </span>
                    </button>
                  )}
                  <div className={`flex space-x-3 ${step === 'url' ? 'ml-auto w-full justify-between' : ''}`}>
                    <button
                      className={`px-3 py-2 text-xs text-dualite-text-primary rounded-md border border-dualite-border-primary hover:border-dualite-border-secondary bg-dualite-background-button hover:bg-dualite-background-button-hover ${
                        isProcessingGithubRepo ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      disabled={isProcessingGithubRepo}
                      onClick={() => {
                        onClose();
                        setError('');
                        setIsLoading(false);
                        setRepoUrl('');
                        setStep('url');
                      }}
                    >
                      {t('modals.github.cancel')}
                    </button>
                    {!isChatStarted && (
                      <button
                        className={`px-3 py-2 text-xs bg-dualite-background-cta hover:bg-dualite-background-ctaHover text-dualite-text-cta rounded-md disabled:bg-gray-300 disabled:cursor-not-allowed flex ${
                          isLoading || isProcessingGithubRepo || (step === 'url' && !isRepoUrlValid)
                            ? 'cursor-not-allowed'
                            : 'cursor-pointer'
                        } items-center justify-center ${isProcessingGithubRepo ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => handleSubmit()}
                        disabled={isLoading || isProcessingGithubRepo || (step === 'url' && !isRepoUrlValid)}
                      >
                        {isLoading ? (
                          <>
                            <div className="w-3.5 h-3.5 rounded-full border-2 border-dualite-text-modal-caption border-t-dualite-text-cta animate-spin mr-1.5" />
                            {isDetectingFramework ? t('modals.github.fetchingDetails') : t('modals.github.processing')}
                          </>
                        ) : step === 'framework' ? (
                          t('modals.github.openRepository')
                        ) : (
                          t('modals.github.next')
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-dualite-text-modal-caption mb-4">{t('modals.github.connectMessage')}</p>
                <button
                  onClick={handleGithubLogin}
                  className="w-full bg-dualite-background-cta hover:bg-dualite-background-ctaHover text-dualite-text-cta rounded-lg py-2 transition-colors font-medium flex items-center justify-center gap-2 text-sm"
                >
                  <div className="i-ph:github-logo text-lg" />
                  {t('modals.github.connectGithub')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Pro Upgrade Modal */}
      <ProUpgradeModal
        isOpen={showProUpgradeModal}
        onClose={() => setShowProUpgradeModal(false)}
        paymentUrl={paymentUrl}
      />
    </Modal>
  );
}
